import { isEmpty, get as LodashGet } from 'lodash';
import { create } from 'zustand';
import { Model, ModelDetails, ModelService } from '../containers/my-central/services';
import { Tracker } from '../shared/tracker/Tracker';
import { usePrescriptionStore } from './prescriptionStore';
import { saveProtocolData } from '../shared/amplitude/amplitudeEventStorage';
import { amplitudeModule } from '../shared/amplitude';
import { MEDICAL_CERTIFICATE_TYPES } from '../interfaces/medicalCertificate';
import { FormulaSummary } from '../interfaces/formula';
export enum FILTER_TYPES {
  ALL = 'all',
  PERSONAL = 'personal',
  INSTITUTIONAL = 'institutional',
  HIGHLIGHTED = 'highlighted',
}

interface GroupedModels {
  all: Model[];
  personal: Model[];
  institutional: Model[];
  highlighted: Model[];
}

interface ModelsLength {
  all: number;
  personal: number;
  institutional: number;
  highlighted: number;
}

export interface ModelStore {
  models: Model[];
  sponsoredModels: Model[];
  highlightedModels: Model[];
  highlightedModelDetails: ModelDetails[];
  sponsoredModelDetails: ModelDetails[];
  fetchModels(prescriberId: number, prescriptionId: number, age?: string): Promise<void>;
  fetchSponsoredModels(prescriberId: number, prescriptionId: number, age?: string): Promise<void>;
  filter: FILTER_TYPES;
  setFilter(filter: FILTER_TYPES): void;
  filteredModels: () => Model[];
  groupedModels: () => GroupedModels;
  modelsLength: () => ModelsLength;
  searchFilter: string;
  setSearchFilter(value: string): void;
  isCreatingModel: boolean;
  setIsCreatingModel(value: boolean): void;
  isInstitutionalModelAdmin: boolean;
  onConfirmModelInsertion(modelId: number, modelDetails: ModelDetails, replaceItems: boolean): Promise<void>;
  fetchHighlightedModelDetails(): Promise<void>;
  fetchSponsoredModelDetails(): Promise<void>;
}

export const useModelStore = create<ModelStore>()((set, get) => ({
  models: [],
  isInstitutionalModelAdmin: false,
  async fetchModels(prescriberId: number, prescriptionId: number, age?: string): Promise<void> {
    const prescriberConfiguration = usePrescriptionStore.getState().prescriberConfiguration;
    const enablePharmaIndustryModel = LodashGet(prescriberConfiguration, 'Features.enablePharmaIndustryModel', false);

    const modelList = await ModelService.fetchModels(prescriberId, prescriptionId, enablePharmaIndustryModel, {
      ...(age && { age }),
    });
    const hightlightedModels = (modelList.data || []).filter((model) => model.destacado);

    set((prev) => {
      return {
        ...prev,
        models: modelList.data || [],
        isInstitutionalModelAdmin: modelList.administradorModeloInstitucional || false,
        highlightedModels: hightlightedModels,
      };
    });
  },
  sponsoredModels: [],
  async fetchSponsoredModels(prescriberId: number, prescriptionId: number, age?: string): Promise<void> {
    const prescriberConfiguration = usePrescriptionStore.getState().prescriberConfiguration;

    const enableSponsoredModel = LodashGet(prescriberConfiguration, 'Features.enableSponsoredModel', false);

    const modelList = await ModelService.fetchSponsoredModels(prescriberId, prescriptionId, enableSponsoredModel, age);
    const hightlightedModels = modelList.data.filter((model) => model.destacado);

    set((prev) => {
      return {
        ...prev,
        sponsoredModels: modelList.data,
        highlightedModels: hightlightedModels,
      };
    });
  },
  filter: FILTER_TYPES.ALL,
  setFilter: (filter: FILTER_TYPES) => set((prev) => ({ ...prev, filter })),
  searchFilter: '',
  setSearchFilter: (value: string) => set((prev) => ({ ...prev, searchFilter: value })),

  groupedModels: () => {
    const filteredModels = get().filteredModels();

    if (isEmpty(filteredModels)) return { institutional: [], personal: [], highlighted: [], all: [] };

    const groupedModels = filteredModels.reduce(
      (acc: GroupedModels, cur: Model) => {
        if (cur.institucional) {
          acc.institutional.push(cur);
        }

        if (!cur.institucional && !cur.destacado) {
          acc.personal.push(cur);
        }

        if (cur.destacado) {
          acc.highlighted.push(cur);
        }

        acc.all.push(cur);
        return acc;
      },
      { institutional: [], personal: [], highlighted: [], all: [] }
    );

    return groupedModels;
  },

  filteredModels: () => {
    const data = get().models;
    const searchFilter = get().searchFilter;

    if (isEmpty(data)) return [];

    return data.filter((model) => model.nome.toLowerCase().match(searchFilter.trim().toLowerCase()));
  },

  modelsLength: () => {
    const groupedModels = get().groupedModels();

    return {
      all: groupedModels.all.length,
      institutional: groupedModels.institutional.length,
      personal: groupedModels.personal.length,
      highlighted: groupedModels.highlighted.length,
    };
  },
  isCreatingModel: false,
  setIsCreatingModel: (value: boolean) => set((prev) => ({ ...prev, isCreatingModel: value })),

  onConfirmModelInsertion: async (modelId: number, modelDetails: ModelDetails, replaceItems: boolean) => {
    const prescription = usePrescriptionStore.getState().prescription;
    try {
      if (!prescription || !modelId || !modelDetails) return;

      usePrescriptionStore.getState().setItemsAddedFromModel(true);

      await usePrescriptionStore
        .getState()
        .addItemsFromPrescription(prescription.idPrescricao, modelDetails.idPrescricao, replaceItems, false);

      const model =
        get().models.find((selectedModel) => selectedModel.id === modelId) ??
        get().sponsoredModels.find((selectedModel) => selectedModel.id === modelId) ??
        get().highlightedModels.find((selectedModel) => selectedModel.id === modelId);

      saveProtocolData(prescription.idPrescricao, {
        id: modelId,
        name: modelDetails.Nome,
        is_sponsored: model?.destacado ?? model?.patrocinado ?? false,
        is_institutional: model?.institucional ?? false,
        created_at: '',
        updated_at: '',
        medications: JSON.stringify((modelDetails.Medicamento ?? []).map((medication) => ({
          id: medication.IdMedicamento,
          name: medication.Nome,
        }))),
        medical_test: JSON.stringify((modelDetails.Exame ?? []).map((test) => ({
          id: String(test.idPrescricaoExame ?? ''),
          name: test.Nome ?? '',
        }))),
        doctors_note: JSON.stringify((modelDetails.Atestados ?? []).map((note) => ({
          id: note.idPrescricaoAtestado,
          type: note.Tipo,
        }))),
        referral: JSON.stringify((modelDetails.Encaminhamentos ?? []).map((referral) => ({
          id: referral.idEncaminhamentoPrescricao,
          specialty: referral.Especialidade?.Nome ?? '',
        }))),
        medical_report: JSON.stringify((modelDetails.Relatorios ?? []).map((report) => ({
          id: report.idPrescricaoLaudoMedico,
          type: report.Titulo ?? '',
        }))),
        compounded_medications: JSON.stringify(((modelDetails.Formulas ?? []) as unknown as FormulaSummary[]).map((compounded) => ({
          id: compounded.id,
          name: compounded.NomeFormula,
        }))),
      });

      Tracker.trackEvent({
        event: 'Model Selected',
        parameters: {
          model: modelDetails.Nome,
        },
      });

      const prescriberConfiguration = usePrescriptionStore.getState().prescriberConfiguration;
      amplitudeModule.protocolSelected({
        screen_name: 'seleção de modelo',
        flow: 'seleção de modelo',
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: usePrescriptionStore.getState().prescription?.Estabelecimento?.Nome ?? '',
        protocol: [{
          id: modelId,
          name: modelDetails.Nome,
          is_sponsored: model?.destacado ?? model?.patrocinado ?? false,
          is_institutional: model?.institucional ?? false,
          created_at: '',
          updated_at: '',
          medications: (modelDetails.Medicamento ?? []).map((m) => ({
            id: m.IdMedicamento,
            name: m.Nome ?? m.Label ?? '',
            is_sponsored: m.isPremiumPlacement ?? false,
          })),
          medical_tests: (modelDetails.Exame ?? []).map((e) => ({
            id: String(e.idPrescricaoExame ?? ''),
            name: e.Nome ?? '',
          })),
          doctors_notes: (modelDetails.Atestados ?? []).map((a) => ({
            id: a.idPrescricaoAtestado,
            name: (MEDICAL_CERTIFICATE_TYPES as Record<string, string>)[a.Tipo] ?? '',
          })),
          referrals: (modelDetails.Encaminhamentos ?? []).map((r) => ({
            id: r.idEncaminhamentoPrescricao,
            name: r.Especialidade?.Nome ?? '',
          })),
          medical_reports: (modelDetails.Relatorios ?? []).map((r) => ({
            id: r.idPrescricaoLaudoMedico,
            name: r.Titulo ?? '',
          })),
          compounded_medications: ((modelDetails.Formulas ?? []) as unknown as FormulaSummary[]).map((f) => ({
            id: f.id,
            name: f.NomeFormula ?? '',
          })),
        }],
      });
    } catch (e) {}
  },
  highlightedModels: [],
  highlightedModelDetails: [],
  async fetchHighlightedModelDetails(): Promise<void> {
    const prescription = usePrescriptionStore.getState().prescription;

    if (!prescription) return;

    try {
      const highlightedModelsDetails = await Promise.all(
        get().highlightedModels.map(async (model) => {
          const details = await ModelService.fetchModelDetails(
            model.id,
            prescription.idPrescricao,
            prescription.Prescritor.idPrescritor
          );
          return { ...details, id: model.id };
        })
      );
      set((prev) => {
        return {
          ...prev,
          highlightedModelDetails: highlightedModelsDetails,
        };
      });
    } catch (error) {}
  },
  sponsoredModelDetails: [],
  async fetchSponsoredModelDetails(): Promise<void> {
    const prescription = usePrescriptionStore.getState().prescription;

    if (!prescription) return;

    try {
      const highlightedModelsDetails = await Promise.all(
        get().sponsoredModels.map(async (model) => {
          const details = await ModelService.fetchModelDetails(
            model.id,
            prescription.idPrescricao,
            prescription.Prescritor.idPrescritor
          );
          return { ...details, id: model.id };
        })
      );
      set((prev) => {
        return {
          ...prev,
          sponsoredModelDetails: highlightedModelsDetails,
        };
      });
    } catch (error) {}
  },
}));
